import { Group, Text } from '@repletoryreach/sdk-ui';
import { StatusBadge, type StatusTone } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { formatDuration } from '../../inbox/components/SlaBadge';
import { categoryTone, swatchColour } from '../lib/data';
import { ticketSla, type SlaInput } from '../lib/sla';

/** A ticket type: its colour and its name. */
export function TypeTag({ type }: { type: { name: string; colour: string | null } }) {
  return (
    <Group gap={6} wrap="nowrap">
      <span className="rr-tickets__swatch" style={{ background: swatchColour(type.colour) }} aria-hidden />
      <Text role="bodySmall">{type.name}</Text>
    </Group>
  );
}

/** A priority: a coloured dot and its name. */
export function PriorityTag({ priority }: { priority: { name: string; colour: string | null } }) {
  return (
    <span className="rr-tickets__priority" style={{ ['--rr-tk-colour' as string]: swatchColour(priority.colour) }}>
      {priority.name}
    </span>
  );
}

/** A status, toned by its category. */
export function StatusTag({ status, size = 'xs' }: { status: { name: string; category: string }; size?: 'xs' | 'sm' }) {
  return <StatusBadge status={categoryTone(status.category)} size={size}>{status.name}</StatusBadge>;
}

/** Due in, overdue or paused, against the ticket's response targets. */
export function TicketSlaBadge({ input, now, size = 'xs' }: { input: SlaInput; now: number; size?: 'xs' | 'sm' }) {
  const t = useT();
  const s = ticketSla(input, now);
  if (s.kind === 'none') return <Text role="caption" c="var(--rr-color-text-muted)">{t('common.none')}</Text>;
  if (s.kind === 'paused') {
    return (
      <span title={t('tickets.sla.pausedHint')}>
        <StatusBadge status="inactive" size={size}>{t('tickets.sla.paused')}</StatusBadge>
      </span>
    );
  }
  const tone: StatusTone = s.kind === 'overdue' ? 'down' : s.soon ? 'warning' : 'healthy';
  const label = s.kind === 'overdue'
    ? (s.ms > 0 ? t('tickets.sla.overdueBy', { time: formatDuration(s.ms) }) : t('tickets.sla.overdue'))
    : t('tickets.sla.dueIn', { time: formatDuration(s.ms) });
  const phase = s.phase === 'first_response' ? t('tickets.sla.firstResponse') : s.phase === 'resolution' ? t('tickets.sla.resolution') : '';
  return (
    <span title={s.due ? t('tickets.sla.dueAt', { phase, when: formatDateTime(s.due) }) : phase}>
      <StatusBadge status={tone} size={size}>{label}</StatusBadge>
    </span>
  );
}

/** The ticket's number, as the client quotes it. */
export function TicketNumber({ number }: { number: string }) {
  return <Text role="bodySmall" fw={600} style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{number}</Text>;
}

